import { useEffect } from 'react';
import { useDeepCompareEffect } from '@/shared/lib/customHooks';
import type { EventSelectionProps } from './index';

const useEventSelection = ({
  componentId = '',
  data = {},
  selectedEventIndex = 0,
}: Pick<EventSelectionProps, 'componentId' | 'data' | 'selectedEventIndex'>) => {
  /**
   * Animates the event-selection tab strip sideways when an arrow is pressed. Looks up the scroll container by id, scoped by the component id when one is set, then runs an interval that shifts scrollLeft by one step each tick, left or right per the direction, and clears the timer once the accumulated amount reaches the requested distance.
   *
   * @param direction - Scroll direction, left to move backwards and anything else forwards.
   * @param speed - Interval delay in milliseconds between scroll steps.
   * @param distance - Total pixels to scroll before the animation stops.
   * @param step - Pixels moved on each tick.
   * @returns Nothing.
   */
  const sideScroll = (direction: string, speed = 25, distance = 100, step = 10) => {
    let scrollAmount = 0;
    const element = document.querySelector(
      `#scroll-container${componentId ? `.${componentId}` : ''}`,
    );
    const slideTimer = setInterval(() => {
      if (direction === 'left') {
        (element as HTMLElement).scrollLeft -= step;
      } else {
        (element as HTMLElement).scrollLeft += step;
      }
      scrollAmount += step;
      if (scrollAmount >= distance) {
        window.clearInterval(slideTimer);
      }
    }, speed);
  };

  useDeepCompareEffect(() => {
    const tabs = document.querySelector(
      `#scroll-container${componentId ? `.${componentId}` : ''}`,
    ) as HTMLElement;
    const leftArrow = document.querySelector(
      `${componentId ? `.${componentId}` : ''}.event-scroll-btn.--left-arrow`,
    ) as HTMLElement;
    const rightArrow = document.querySelector(
      `${componentId ? `.${componentId}` : ''}.event-scroll-btn.--right-arrow`,
    ) as HTMLElement;
    leftArrow.style.display = 'none';
    rightArrow.style.display = 'none';
    if (tabs.scrollWidth > tabs.clientWidth) {
      rightArrow.style.display = 'block';
    }
    // window.addEventListener('wheel', (e) => {
    //   if (e.deltaY > 0) tabs.scrollLeft += 50;
    //   else tabs.scrollLeft -= 50;
    // });
    /**
     * Shows or hides the event-selection tab strip's scroll arrows to match the current scroll position. Hides the left arrow at scrollLeft zero and hides the right arrow once scrollLeft sits within three pixels of the maximum scrollable offset, showing each otherwise by toggling the elements' display style directly.
     *
     * @returns Nothing.
     */
    const handleScroll = () => {
      if (tabs.scrollLeft === 0) {
        leftArrow.style.display = 'none';
      } else {
        leftArrow.style.display = 'block';
      }
      const isInHidenArea = tabs.scrollWidth - tabs.clientWidth;
      if (tabs.scrollLeft < isInHidenArea + 3 && tabs.scrollLeft > isInHidenArea - 3) {
        rightArrow.style.display = 'none';
      } else {
        rightArrow.style.display = 'block';
      }
    };
    tabs.addEventListener('scroll', handleScroll, { passive: true });
  }, [componentId, data]);

  useEffect(() => {
    // scroll to selected event
    const tabs = document.querySelector(
      `#scroll-container${componentId ? `.${componentId}` : ''}`,
    ) as HTMLElement;
    const selectedTab = tabs.querySelector('.tab-item.--active') as HTMLElement;
    if (selectedTab) {
      tabs.scrollLeft = selectedTab.offsetLeft - tabs.clientWidth / 2 + selectedTab.clientWidth / 2;
    }
  }, [selectedEventIndex, componentId]);

  return { sideScroll };
};

export default useEventSelection;
